"use client";

import { useState, useEffect, useCallback, useRef } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface UserProfile {
  username: string;
  email: string;
  full_name: string;
  phone: string;
  street: string;
  zip_code: string;
  city: string;
  country: string;
  role: string;
  odoo_partner_id: number | null;
  auto_save_enabled: boolean;
  created_at: string | null;
}

interface Booking {
  booking_id: string;
  user_name: string;
  tool_uuids: string[];
  tool_names?: string[];
  start_date: string;
  end_date: string;
  status: string;
  billing_type: string;
  total_cost?: number;
  created_at: string;
}

interface Invoice {
  id: number;
  name: string;
  state: string;
  amount_total: number;
  currency: string;
  invoice_date: string | null;
  due_date: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const TOKEN_KEY = "account_token";
const USER_KEY = "account_user";

function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

function getStoredUser(): { username: string; role: string } | null {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(USER_KEY);
  return raw ? JSON.parse(raw) : null;
}

function authHeaders(token: string) {
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

async function apiFetch(path: string, token: string, options?: RequestInit) {
  const res = await fetch(`/api/${path}`, {
    ...options,
    headers: { ...authHeaders(token), ...(options?.headers ?? {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || res.statusText);
  }
  return res.json();
}

const STATUS_LABEL: Record<string, string> = {
  active: "Aktiv",
  returned: "Afleveret",
  overdue: "Forsinket",
  cancelled: "Annulleret",
};
const STATUS_COLOR: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  returned: "bg-gray-100 text-gray-700",
  overdue: "bg-red-100 text-red-700",
  cancelled: "bg-yellow-100 text-yellow-800",
};
const INVOICE_STATE: Record<string, string> = {
  draft: "Kladde",
  posted: "Bogført",
  paid: "Betalt",
  cancel: "Annulleret",
};
const INVOICE_STATE_COLOR: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  posted: "bg-blue-100 text-blue-700",
  paid: "bg-green-100 text-green-800",
  cancel: "bg-red-100 text-red-700",
};

// ─────────────────────────────────────────────────────────────────────────────
// Login form (password + OTP tabs + forgot-password)
// ─────────────────────────────────────────────────────────────────────────────

function LoginForm({ onSuccess }: { onSuccess: (token: string, username: string, role: string) => void }) {
  const [mode, setMode] = useState<"password" | "otp">("password");

  // Password login state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // OTP state
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");

  // Forgot-password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Forkert brugernavn eller adgangskode");
      }
      const data = await res.json();
      localStorage.setItem(TOKEN_KEY, data.access_token);
      localStorage.setItem(USER_KEY, JSON.stringify({ username: data.username, role: data.role, is_superuser: data.is_superuser ?? false }));
      onSuccess(data.access_token, data.username, data.role);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Fejl ved login");
    } finally {
      setLoading(false);
    }
  };

  const requestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError("");
    setOtpLoading(true);
    try {
      await fetch("/api/auth/otp/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: otpEmail }),
      });
      setOtpSent(true);
    } catch {
      setOtpError("Kunne ikke sende kode. Prøv igen.");
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError("");
    setOtpLoading(true);
    try {
      const res = await fetch("/api/auth/otp/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: otpEmail, otp: otpCode }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Ugyldig kode");
      }
      const data = await res.json();
      localStorage.setItem(TOKEN_KEY, data.access_token);
      localStorage.setItem(USER_KEY, JSON.stringify({ username: data.username, role: data.role, is_superuser: data.is_superuser ?? false }));
      onSuccess(data.access_token, data.username, data.role);
    } catch (e: unknown) {
      setOtpError(e instanceof Error ? e.message : "Fejl ved verificering");
    } finally {
      setOtpLoading(false);
    }
  };

  const sendForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMsg("");
    try {
      await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      setForgotMsg("Hvis e-mailen er registreret, modtager du et link om et øjeblik.");
    } catch {
      setForgotMsg("Fejl. Prøv igen.");
    } finally {
      setForgotLoading(false);
    }
  };

  if (showForgot) {
    return (
      <div className="space-y-4">
        <button onClick={() => setShowForgot(false)} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
          ← Tilbage til login
        </button>
        <h3 className="font-semibold text-gray-800">Glemt adgangskode?</h3>
        <p className="text-sm text-gray-500">Indtast din e-mail — vi sender dig et nulstillingslink.</p>
        <form onSubmit={sendForgot} className="space-y-3">
          <input
            type="email"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            required
            placeholder="din@email.dk"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500"
          />
          {forgotMsg && (
            <p className={`text-sm px-3 py-2 rounded-lg ${forgotMsg.startsWith("Fejl") ? "bg-red-50 text-red-600" : "bg-green-50 text-green-700"}`}>
              {forgotMsg}
            </p>
          )}
          <button type="submit" disabled={forgotLoading}
            className="w-full py-2.5 rounded-xl text-white font-semibold disabled:opacity-50 text-sm"
            style={{ backgroundColor: "#2d6a4f" }}>
            {forgotLoading ? "Sender..." : "Send nulstillingslink"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Login mode toggle */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {(["password", "otp"] as const).map((m) => (
          <button key={m} onClick={() => { setMode(m); setError(""); setOtpError(""); }}
            className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              mode === m ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
            }`}>
            {m === "password" ? "🔑 Adgangskode" : "📧 Engangskode (OTP)"}
          </button>
        ))}
      </div>

      {mode === "password" ? (
        <form onSubmit={handlePasswordLogin} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Brugernavn</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500"
              placeholder="Dit brugernavn" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Adgangskode</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password" required
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500"
              placeholder="Din adgangskode" />
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
          <button type="submit" disabled={loading}
            className="w-full py-3 rounded-xl text-white font-semibold disabled:opacity-50 text-sm"
            style={{ backgroundColor: "#2d6a4f" }}>
            {loading ? "Logger ind..." : "Log ind"}
          </button>
          <div className="text-center">
            <button type="button" onClick={() => setShowForgot(true)}
              className="text-xs text-green-700 hover:underline">
              Glemt adgangskode?
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          {!otpSent ? (
            <form onSubmit={requestOtp} className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">E-mailadresse</label>
                <input type="email" value={otpEmail} onChange={(e) => setOtpEmail(e.target.value)} required
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500"
                  placeholder="din@email.dk" />
              </div>
              <p className="text-xs text-gray-400">Vi sender en 6-cifret kode til din e-mail.</p>
              {otpError && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{otpError}</p>}
              <button type="submit" disabled={otpLoading}
                className="w-full py-3 rounded-xl text-white font-semibold disabled:opacity-50 text-sm"
                style={{ backgroundColor: "#2d6a4f" }}>
                {otpLoading ? "Sender..." : "Send engangskode"}
              </button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-xl px-3 py-2.5 text-sm text-green-700">
                📧 Kode sendt til <strong>{otpEmail}</strong>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Engangskode</label>
                <input value={otpCode} onChange={(e) => setOtpCode(e.target.value)} required
                  maxLength={6} inputMode="numeric" pattern="\d{6}"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500 tracking-[0.5em] text-center font-mono text-lg"
                  placeholder="_ _ _ _ _ _" autoFocus />
              </div>
              {otpError && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{otpError}</p>}
              <button type="submit" disabled={otpLoading}
                className="w-full py-3 rounded-xl text-white font-semibold disabled:opacity-50 text-sm"
                style={{ backgroundColor: "#2d6a4f" }}>
                {otpLoading ? "Verificerer..." : "Log ind med kode"}
              </button>
              <div className="text-center">
                <button type="button" onClick={() => { setOtpSent(false); setOtpCode(""); setOtpError(""); }}
                  className="text-xs text-gray-500 hover:text-gray-700">
                  Send ny kode
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Register form (with real-time email uniqueness check)
// ─────────────────────────────────────────────────────────────────────────────

function RegisterForm({ onSuccess }: { onSuccess: (token: string, username: string, role: string) => void }) {
  const [form, setForm] = useState({
    username: "", password: "", passwordConfirm: "",
    full_name: "", email: "", phone: "",
    street: "", zip_code: "", city: "", country: "Danmark",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Email uniqueness check
  const [emailStatus, setEmailStatus] = useState<"idle" | "checking" | "taken" | "ok">("idle");
  const emailTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, email: v }));
    setEmailStatus("idle");
    if (emailTimer.current) clearTimeout(emailTimer.current);
    if (!v || !v.includes("@")) return;
    setEmailStatus("checking");
    emailTimer.current = setTimeout(async () => {
      try {
        const res = await fetch("/api/auth/check-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: v }),
        });
        const data = await res.json();
        setEmailStatus(data.available ? "ok" : "taken");
      } catch {
        setEmailStatus("idle");
      }
    }, 500);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (emailStatus === "taken") { setError("E-mailen er allerede registreret"); return; }
    if (form.password !== form.passwordConfirm) {
      setError("Adgangskoderne stemmer ikke overens");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: form.username,
          password: form.password,
          full_name: form.full_name,
          email: form.email,
          phone: form.phone,
          street: form.street,
          zip_code: form.zip_code,
          city: form.city,
          country: form.country,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Oprettelse mislykkedes");
      }
      const data = await res.json();
      localStorage.setItem(TOKEN_KEY, data.access_token);
      localStorage.setItem(USER_KEY, JSON.stringify({ username: data.username, role: data.role, is_superuser: data.is_superuser ?? false }));
      onSuccess(data.access_token, data.username, data.role);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Fejl ved oprettelse");
    } finally {
      setLoading(false);
    }
  };

  const emailBorderClass =
    emailStatus === "taken" ? "border-red-400 focus:border-red-500" :
    emailStatus === "ok"    ? "border-green-400 focus:border-green-500" :
                              "border-gray-200 focus:border-green-500";

  return (
    <form onSubmit={submit} className="space-y-4">
      {/* Name */}
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1">Fulde navn *</label>
        <input value={form.full_name} onChange={set("full_name")} required
          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500"
          placeholder="For- og efternavn" />
      </div>

      {/* Username */}
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1">Brugernavn *</label>
        <input value={form.username} onChange={set("username")} required
          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500"
          placeholder="Vælg et brugernavn (min. 3 tegn)" />
      </div>

      {/* Email & Phone */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">
            E-mail *{" "}
            {emailStatus === "checking" && <span className="text-gray-400 font-normal text-xs">tjekker…</span>}
            {emailStatus === "taken"    && <span className="text-red-500 font-normal text-xs">● Allerede i brug</span>}
            {emailStatus === "ok"       && <span className="text-green-600 font-normal text-xs">✓ Ledig</span>}
          </label>
          <input type="email" value={form.email} onChange={handleEmailChange} required
            className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none ${emailBorderClass}`}
            placeholder="din@email.dk" />
          {emailStatus === "taken" && (
            <p className="text-xs text-red-500 mt-1">
              Denne e-mail er allerede tilknyttet en konto.{" "}
              <span className="underline cursor-pointer text-green-700" onClick={() => {
                // Dispatch click up to parent to switch to login — handled by AuthGate
                window.dispatchEvent(new CustomEvent("switch-to-login"));
              }}>Log ind i stedet?</span>
            </p>
          )}
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Mobilnummer</label>
          <input type="tel" value={form.phone} onChange={set("phone")}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500"
            placeholder="+45 12 34 56 78" />
        </div>
      </div>

      {/* Address */}
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1">Adresse</label>
        <input value={form.street} onChange={set("street")}
          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500 mb-2"
          placeholder="Vejnavn og husnummer" />
        <div className="grid grid-cols-3 gap-2">
          <input value={form.zip_code} onChange={set("zip_code")}
            className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500"
            placeholder="Postnr." />
          <input value={form.city} onChange={set("city")}
            className="col-span-2 px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500"
            placeholder="By" />
        </div>
      </div>

      {/* Password */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Adgangskode *</label>
          <input type="password" value={form.password} onChange={set("password")} required autoComplete="new-password"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500"
            placeholder="Min. 6 tegn" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Gentag adgangskode *</label>
          <input type="password" value={form.passwordConfirm} onChange={set("passwordConfirm")} required autoComplete="new-password"
            className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none ${
              form.passwordConfirm && form.password !== form.passwordConfirm
                ? "border-red-300 focus:border-red-400"
                : "border-gray-200 focus:border-green-500"
            }`}
            placeholder="Gentag adgangskoden" />
        </div>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

      <button type="submit" disabled={loading || emailStatus === "taken" || emailStatus === "checking"}
        className="w-full py-3 rounded-xl text-white font-semibold disabled:opacity-50 text-sm"
        style={{ backgroundColor: "#2d6a4f" }}>
        {loading ? "Opretter konto..." : "Opret konto"}
      </button>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reset-password form (used when ?reset_token= is present in URL)
// ─────────────────────────────────────────────────────────────────────────────

function ResetPasswordForm({ token, onSuccess }: {
  token: string;
  onSuccess: (jwt: string, username: string, role: string) => void;
}) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pw !== pw2) { setError("Adgangskoderne stemmer ikke overens"); return; }
    if (pw.length < 8) { setError("Minimum 8 tegn"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: pw }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Linket er ugyldigt eller udløbet");
      }
      const data = await res.json();
      localStorage.setItem(TOKEN_KEY, data.access_token);
      localStorage.setItem(USER_KEY, JSON.stringify({ username: data.username, role: data.role, is_superuser: data.is_superuser ?? false }));
      onSuccess(data.access_token, data.username, data.role);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Fejl");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-gray-800">Vælg ny adgangskode</h3>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Ny adgangskode <span className="text-gray-400 font-normal">(min. 8 tegn)</span></label>
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8}
            autoComplete="new-password"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Gentag</label>
          <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required
            autoComplete="new-password"
            className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none ${
              pw2 && pw !== pw2 ? "border-red-300" : "border-gray-200 focus:border-green-500"
            }`} />
        </div>
        {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
        <button type="submit" disabled={loading}
          className="w-full py-3 rounded-xl text-white font-semibold disabled:opacity-50 text-sm"
          style={{ backgroundColor: "#2d6a4f" }}>
          {loading ? "Gemmer..." : "Gem ny adgangskode"}
        </button>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bookings tab
// ─────────────────────────────────────────────────────────────────────────────

function BookingsTab({ token }: { token: string }) {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    apiFetch("toollib/bookings/me", token)
      .then((d) => setBookings(d.bookings ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="py-12 text-center text-gray-400 text-sm">Henter bookinger...</div>;
  if (error) return <div className="py-6 text-sm text-red-600 bg-red-50 rounded-xl px-4">{error}</div>;
  if (bookings.length === 0) return (
    <div className="py-12 text-center text-gray-400">
      <div className="text-4xl mb-3">📦</div>
      <p className="text-sm">Ingen bookinger endnu</p>
      <p className="text-xs mt-1 text-gray-400">Dine reservationer vises her, når du har bestilt værktøjer.</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {bookings.map((b) => {
        const start = new Date(b.start_date).toLocaleDateString("da-DK", { day: "2-digit", month: "short" });
        const end   = new Date(b.end_date).toLocaleDateString("da-DK",   { day: "2-digit", month: "short", year: "numeric" });
        return (
          <div key={b.booking_id} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 font-mono">#{b.booking_id.slice(-8)}</p>
                {b.tool_names && b.tool_names.length > 0 ? (
                  <p className="font-medium text-gray-900 text-sm mt-0.5">{b.tool_names.join(", ")}</p>
                ) : (
                  <p className="font-medium text-gray-900 text-sm mt-0.5">{b.tool_uuids.length} værktøj{b.tool_uuids.length !== 1 ? "er" : ""}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">📅 {start} → {end}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {b.billing_type === "subscription" ? "🔄 Abonnement" : "💳 Enkelt lån"}
                  {b.total_cost ? ` · ${b.total_cost.toFixed(2)} kr` : ""}
                </p>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ${STATUS_COLOR[b.status] ?? "bg-gray-100 text-gray-600"}`}>
                {STATUS_LABEL[b.status] ?? b.status}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Billing tab
// ─────────────────────────────────────────────────────────────────────────────

function BillingTab({ token, profile }: { token: string; profile: UserProfile }) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [subMonths, setSubMonths] = useState(1);
  const [subLoading, setSubLoading] = useState(false);
  const [subMsg, setSubMsg] = useState("");

  const load = useCallback(async () => {
    if (!profile.odoo_partner_id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [invData, balData] = await Promise.all([
        apiFetch(`users/${profile.username}/odoo/invoices`, token),
        apiFetch(`users/${profile.username}/odoo/balance`, token),
      ]);
      setInvoices(invData.invoices ?? []);
      setBalance(balData.balance ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Fejl");
    } finally {
      setLoading(false);
    }
  }, [token, profile]);

  useEffect(() => { load(); }, [load]);

  const createSubInvoice = async () => {
    setSubLoading(true);
    setSubMsg("");
    try {
      await apiFetch(`users/${profile.username}/odoo/invoice/subscription`, token, {
        method: "POST",
        body: JSON.stringify({ months: subMonths }),
      });
      setSubMsg("✅ Abonnementsfaktura oprettet");
      load();
    } catch (e: unknown) {
      setSubMsg(`❌ ${e instanceof Error ? e.message : "Fejl"}`);
    } finally {
      setSubLoading(false);
    }
  };

  if (!profile.odoo_partner_id) return (
    <div className="py-10 text-center text-gray-400">
      <div className="text-4xl mb-3">🔗</div>
      <p className="text-sm">Ingen Odoo-konto tilknyttet</p>
      <p className="text-xs mt-1">Kontakt biblioteket for at aktivere fakturering</p>
    </div>
  );

  if (loading) return <div className="py-12 text-center text-gray-400 text-sm">Henter fakturaer...</div>;
  if (error)   return <div className="py-6 text-sm text-red-600 bg-red-50 rounded-xl px-4">{error}</div>;

  return (
    <div className="space-y-5">
      {/* Balance card */}
      {balance !== null && (
        <div className={`rounded-2xl p-4 border ${balance <= 0 ? "bg-green-50 border-green-200" : "bg-orange-50 border-orange-200"}`}>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Saldo</p>
          <p className={`text-3xl font-bold mt-1 ${balance <= 0 ? "text-green-700" : "text-orange-700"}`}>
            {balance <= 0 ? `${Math.abs(balance).toFixed(2)} kr` : `${balance.toFixed(2)} kr skyldige`}
          </p>
          {balance > 0 && <p className="text-xs text-orange-600 mt-1">Du har udestående betalinger</p>}
        </div>
      )}

      {/* Create subscription invoice */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
        <h3 className="font-semibold text-gray-900 text-sm mb-3">Opret abonnementsfaktura</h3>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Måneder:</label>
            <select
              value={subMonths}
              onChange={(e) => setSubMonths(Number(e.target.value))}
              className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none"
            >
              {[1, 3, 6, 12].map((m) => (
                <option key={m} value={m}>{m} md.</option>
              ))}
            </select>
          </div>
          <button
            onClick={createSubInvoice}
            disabled={subLoading}
            className="px-4 py-1.5 text-sm rounded-lg text-white font-medium disabled:opacity-50"
            style={{ backgroundColor: "#2d6a4f" }}
          >
            {subLoading ? "Opretter..." : "Opret faktura"}
          </button>
        </div>
        {subMsg && <p className="text-xs mt-2 text-gray-600">{subMsg}</p>}
      </div>

      {/* Invoice list */}
      {invoices.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-sm">Ingen fakturaer</div>
      ) : (
        <div className="space-y-2">
          <h3 className="font-semibold text-gray-900 text-sm">Fakturahistorik</h3>
          {invoices.map((inv) => (
            <div key={inv.id} className="bg-white rounded-xl border border-gray-100 p-3.5 shadow-sm flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 text-sm">{inv.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {inv.invoice_date
                    ? new Date(inv.invoice_date).toLocaleDateString("da-DK")
                    : "Ingen dato"
                  }
                  {inv.due_date ? ` · Forfald: ${new Date(inv.due_date).toLocaleDateString("da-DK")}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="font-semibold text-sm text-gray-900">{inv.amount_total.toFixed(2)} {inv.currency}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${INVOICE_STATE_COLOR[inv.state] ?? "bg-gray-100 text-gray-600"}`}>
                  {INVOICE_STATE[inv.state] ?? inv.state}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Profile tab
// ─────────────────────────────────────────────────────────────────────────────

function ProfileTab({ token, profile, onUpdated }: {
  token: string;
  profile: UserProfile;
  onUpdated: (p: UserProfile) => void;
}) {
  const [form, setForm] = useState({
    full_name: profile.full_name,
    email: profile.email,
    phone: profile.phone,
    street: profile.street,
    zip_code: profile.zip_code,
    city: profile.city,
    country: profile.country || "Danmark",
    auto_save_enabled: profile.auto_save_enabled ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // Password change
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState("");

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const setPw = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setPwForm((f) => ({ ...f, [k]: e.target.value }));

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    try {
      const updated = await apiFetch(`users/${profile.username}`, token, {
        method: "PUT",
        body: JSON.stringify(form),
      });
      onUpdated({ ...profile, ...updated });
      setMsg("✅ Profil opdateret");
    } catch (e: unknown) {
      setMsg(`❌ ${e instanceof Error ? e.message : "Fejl"}`);
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      setPwMsg("❌ Adgangskoderne stemmer ikke overens");
      return;
    }
    setPwSaving(true);
    setPwMsg("");
    try {
      await apiFetch(`users/${profile.username}/change-password`, token, {
        method: "POST",
        body: JSON.stringify({ current_password: pwForm.current_password, new_password: pwForm.new_password }),
      });
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      setPwMsg("✅ Adgangskode ændret");
    } catch (e: unknown) {
      setPwMsg(`❌ ${e instanceof Error ? e.message : "Fejl"}`);
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile form */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4">Personlige oplysninger</h3>
        <form onSubmit={saveProfile} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Fulde navn</label>
            <input value={form.full_name} onChange={set("full_name")}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">E-mail</label>
              <input type="email" value={form.email} onChange={set("email")}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Mobilnummer</label>
              <input type="tel" value={form.phone} onChange={set("phone")}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Adresse</label>
            <input value={form.street} onChange={set("street")} placeholder="Vejnavn og nr."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500 mb-2" />
            <div className="grid grid-cols-3 gap-2">
              <input value={form.zip_code} onChange={set("zip_code")} placeholder="Postnr."
                className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500" />
              <input value={form.city} onChange={set("city")} placeholder="By"
                className="col-span-2 px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500" />
            </div>
          </div>
          {msg && <p className="text-sm">{msg}</p>}
          <div className="flex items-center gap-3 py-1">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={form.auto_save_enabled}
                onChange={(e) => setForm((f) => ({ ...f, auto_save_enabled: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-10 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600" />
            </label>
            <div>
              <p className="text-sm font-medium text-gray-700">Automatisk lagring</p>
              <p className="text-xs text-gray-400">Gem automatisk mens du redigerer Project Canvas, Økonomi og Imagination Sundial</p>
            </div>
          </div>
          <button type="submit" disabled={saving}
            className="px-6 py-2.5 text-sm rounded-xl text-white font-medium disabled:opacity-50"
            style={{ backgroundColor: "#2d6a4f" }}>
            {saving ? "Gemmer..." : "Gem ændringer"}
          </button>
        </form>
      </div>

      {/* Account info */}
      <div className="bg-gray-50 rounded-2xl border border-gray-100 p-4">
        <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">Kontooplysninger</p>
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Brugernavn</span>
            <span className="font-medium text-gray-900">{profile.username}</span>
          </div>
          {profile.odoo_partner_id && (
            <div className="flex justify-between">
              <span className="text-gray-500">Odoo kundekode</span>
              <span className="font-medium text-gray-900">#{profile.odoo_partner_id}</span>
            </div>
          )}
          {profile.created_at && (
            <div className="flex justify-between">
              <span className="text-gray-500">Oprettet</span>
              <span className="font-medium text-gray-900">
                {new Date(profile.created_at).toLocaleDateString("da-DK")}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Change password */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4">Skift adgangskode</h3>
        <form onSubmit={changePassword} className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Nuværende adgangskode</label>
            <input type="password" value={pwForm.current_password} onChange={setPw("current_password")} required
              autoComplete="current-password"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Ny adgangskode <span className="text-gray-400 font-normal">(min. 8 tegn)</span></label>
              <input type="password" value={pwForm.new_password} onChange={setPw("new_password")} required minLength={8}
                autoComplete="new-password"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Gentag</label>
              <input type="password" value={pwForm.confirm} onChange={setPw("confirm")} required
                autoComplete="new-password"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500" />
            </div>
          </div>
          {pwMsg && <p className="text-sm">{pwMsg}</p>}
          <button type="submit" disabled={pwSaving}
            className="px-6 py-2.5 text-sm rounded-xl text-white font-medium disabled:opacity-50"
            style={{ backgroundColor: "#2d6a4f" }}>
            {pwSaving ? "Ændrer..." : "Skift adgangskode"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main dashboard (authenticated)
// ─────────────────────────────────────────────────────────────────────────────

type Tab = "bookings" | "billing" | "profile";

function Dashboard({ token, username, onLogout }: {
  token: string;
  username: string;
  onLogout: () => void;
}) {
  const [tab, setTab] = useState<Tab>("bookings");
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  useEffect(() => {
    apiFetch(`users/me`, token)
      .then((p) => setProfile(p))
      .catch(() => setProfile(null))
      .finally(() => setLoadingProfile(false));
  }, [token]);

  const TABS: { key: Tab; label: string; icon: string }[] = [
    { key: "bookings", label: "Mine bookinger", icon: "📦" },
    { key: "billing",  label: "Fakturering",    icon: "💳" },
    { key: "profile",  label: "Profil",         icon: "👤" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm"
              style={{ backgroundColor: "#2d6a4f" }}>
              {username.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">
                {loadingProfile ? username : (profile?.full_name || username)}
              </p>
              <p className="text-xs text-gray-500">Værktøjsbibliotek · Konto</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">
              🏠 Portal
            </a>
            <a href="/?view=tools" className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">
              🔧 Værktøjsbibliotek
            </a>
            <button
              onClick={onLogout}
              className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
            >
              Log ud
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-2xl mx-auto px-4 flex gap-1 pb-3">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 py-2 text-xs font-medium rounded-xl transition-colors ${
                tab === t.key
                  ? "text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              style={tab === t.key ? { backgroundColor: "#2d6a4f" } : {}}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-5">
        {tab === "bookings" && <BookingsTab token={token} />}
        {tab === "billing" && profile && <BillingTab token={token} profile={profile} />}
        {tab === "billing" && !profile && !loadingProfile && (
          <div className="py-10 text-center text-gray-400 text-sm">Profil ikke tilgængelig</div>
        )}
        {tab === "profile" && profile && (
          <ProfileTab token={token} profile={profile} onUpdated={setProfile} />
        )}
        {tab === "profile" && !profile && !loadingProfile && (
          <div className="py-10 text-center text-gray-400 text-sm">Profil ikke tilgængelig</div>
        )}
        {(tab === "billing" || tab === "profile") && loadingProfile && (
          <div className="py-12 text-center text-gray-400 text-sm">Henter profil...</div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Auth gate (login / register)
// ─────────────────────────────────────────────────────────────────────────────

function AuthGate({ onSuccess }: { onSuccess: (token: string, username: string, role: string) => void }) {
  const [view, setView] = useState<"login" | "register" | "reset">("login");
  const [resetToken, setResetToken] = useState("");

  useEffect(() => {
    const rt = new URLSearchParams(window.location.search).get("reset_token");
    if (rt) { setResetToken(rt); setView("reset"); }
  }, []);

  // Allow RegisterForm to dispatch "switch-to-login" event
  useEffect(() => {
    const handler = () => setView("login");
    window.addEventListener("switch-to-login", handler);
    return () => window.removeEventListener("switch-to-login", handler);
  }, []);

  const isTabView = view === "login" || view === "register";

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo / heading */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl text-3xl mb-4 shadow-md bg-white">
            🔧
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Værktøjsbibliotek</h1>
          <p className="text-sm text-gray-500 mt-1">ApotekHaslev · Min konto</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Toggle tabs — only show for login/register */}
          {isTabView && (
            <div className="flex border-b border-gray-100">
              <button
                onClick={() => setView("login")}
                className={`flex-1 py-4 text-sm font-semibold transition-colors ${
                  view === "login" ? "text-green-800 border-b-2 border-green-700 bg-green-50" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Log ind
              </button>
              <button
                onClick={() => setView("register")}
                className={`flex-1 py-4 text-sm font-semibold transition-colors ${
                  view === "register" ? "text-green-800 border-b-2 border-green-700 bg-green-50" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Opret konto
              </button>
            </div>
          )}

          <div className="p-6">
            {view === "login" && (
              <>
                <LoginForm onSuccess={onSuccess} />
                <p className="text-center text-xs text-gray-400 mt-4">
                  Ingen konto?{" "}
                  <button onClick={() => setView("register")} className="text-green-700 hover:underline font-medium">
                    Opret konto gratis
                  </button>
                </p>
              </>
            )}
            {view === "register" && (
              <>
                <RegisterForm onSuccess={onSuccess} />
                <p className="text-center text-xs text-gray-400 mt-4">
                  Har du allerede en konto?{" "}
                  <button onClick={() => setView("login")} className="text-green-700 hover:underline font-medium">
                    Log ind her
                  </button>
                </p>
              </>
            )}
            {view === "reset" && (
              <ResetPasswordForm token={resetToken} onSuccess={onSuccess} />
            )}
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          ApotekHaslev Værktøjs- og Materialebibliotek
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page root
// ─────────────────────────────────────────────────────────────────────────────

export default function AccountPage() {
  const [token, setToken] = useState<string | null>(null);
  const [username, setUsername] = useState<string>("");
  const [initialized, setInitialized] = useState(false);

  // Restore from localStorage on first render
  useEffect(() => {
    const t = getToken();
    const u = getStoredUser();
    if (t && u) {
      // Verify token is still valid
      fetch("/api/auth/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: t }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.valid) {
            setToken(t);
            setUsername(u.username);
          } else {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
          }
        })
        .catch(() => {})
        .finally(() => setInitialized(true));
    } else {
      setInitialized(true);
    }
  }, []);

  const handleLoginSuccess = (t: string, u: string, _role: string) => {
    setToken(t);
    setUsername(u);
    // Redirect to ?next= if present and safe (relative path only)
    const next = new URLSearchParams(window.location.search).get("next");
    if (next && next.startsWith("/")) {
      window.location.href = next;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUsername("");
  };

  if (!initialized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400 text-sm">Indlæser...</div>
      </div>
    );
  }

  if (!token) {
    return <AuthGate onSuccess={handleLoginSuccess} />;
  }

  return <Dashboard token={token} username={username} onLogout={handleLogout} />;
}
